/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import { useRouter } from "next/navigation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Slider from "react-slick";
import { Dialog, Modal } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import ButtonTabWrapper from "@/components/atoms/button-tab-wrapper/button-tab-wrapper";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import CancellationCoverApiErrorModal from "@/components/molecules/api-error-modal/cancellation-cover-api-error-modal";
import ComparisionSideDrawer from "@/components/molecules/quote-comparison-table/comparison-side-drawer";
import CoverDetailsDrawer from "@/components/molecules/quote-section/quote-cover/cover-details-drawer/cover-details-drawer";
import CancellationCoverModal from "@/components/molecules/quote-section/quote-cover/modal/cancellation-cover-modal";
import StAmtTogleModal from "@/components/molecules/quote-section/quote-cover/st-amt-togle-modal/st-amt-togle-modal";
import TripTypeToggle from "@/components/molecules/trip-type-toggle/trip-type-toggle";
import { OptimizelyExperimentsEnum } from "@/config/app-constant";
import {
  ANNUAL_MULTI_TRIP,
  APPLICATION,
  PRODUCT,
  SINGLE_TRIP,
  ST_AMT_SWITCH_DATE_MODAL_HEIGHT,
  ST_AMT_SWITCH_DATE_MODAL_SAFE_TOP_PERCENTAGE,
} from "@/constants";
import useUpdateTripDetails, {
  UpdateProps,
} from "@/hooks/api/quote/update-trip-details";
import useGtmQuotePolicyOptions from "@/hooks/gtm/use-gtm-quote-policy-options";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import useModalPosition from "@/hooks/use-modal-position/use-modal-position";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useLoading } from "@/providers/loading-provider";
import {
  BenefitCopyDataType,
  ComparisonTableAreaContentType,
  CoverAreaDataType,
  CoverContentType,
  CoverDrawerDetailsDataType,
  CancellationCovers,
} from "@/schema/quote-page/page.schema";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import {
  clearQuote,
  setCoverLevelChangedState,
  updateQuote,
} from "@/store/slices/quote/quote.slice";
import { brand } from "@/tailwind.config";
import { QuoteData } from "@/types/quote/quote-data";
import isCruiseImpact from "@/utils/check-cruise-impact-base-on-brand.utils";
import defaultCoverLevel from "@/utils/deafult-cover-level.utils";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import QuoteCoverTypeMap from "@/utils/gtm/cover-type-map";
import QuoteDataMap from "@/utils/gtm/quote-data-map";

import getRelatedCoverData from "./dynamic-data-map/magnolia-data-map";
import MapQuoteCard from "./dynamic-data-map/map-quote-card";
import SlickButton from "./slick-button/slick-button";
import { MobileQuoteComparison } from "../mobile-quote-comparison/mobile-quote-comparison";
import {
  calculateAmtPriceDifferenceByBrand,
  createBtnTabs,
  createTripTypeToggleHandler,
  getBrandUIConfig,
  hasOnlyOneCoverEligible,
} from "./utils/quote-cover.utils";

// Edit start date or region button component
const EditStartDateButton = ({
  t,
  onClick,
}: {
  t: (key: string, fallback?: string) => string;
  onClick?: () => void;
}) => (
  <button
    type="button"
    id="edit-start-date-region"
    className="text-[14px] font-semibold-font font-[600] leading-[150%] text-[#005A5E] hover:text-interactive-secondary-hovered underline inline align-baseline bg-transparent border-0 p-0 cursor-pointer"
    onClick={onClick}
  >
    {t("edit_start_date_or_region", "Edit start date or region")}
  </button>
);

const AmtAreaText = (
  t: (key: string, fallback?: string) => string,
  isPriceToggleEnabled: boolean = false,
  onEditClick?: () => void
) => ({
  UK: (
    <p>
      {t(
        "amt_area_uk",
        "United Kingdom (England, Scotland, Wales, and Northern Ireland), Channel Islands, and Isle of Man"
      )}
      {isPriceToggleEnabled ? (
        <>
          . <EditStartDateButton t={t} onClick={onEditClick} />
        </>
      ) : null}
    </p>
  ),
  EUROPE_HR: (
    <p>
      <span className="font-[600]">{t("europe", "Europe")}</span>{" "}
      {t(
        "amt_area_europe_hr_desc",
        "including Cyprus, Egypt, Greece, Malta, Morocco, Spain, Turkey & Tunisia"
      )}
      {isPriceToggleEnabled ? (
        <>
          . <EditStartDateButton t={t} onClick={onEditClick} />
        </>
      ) : null}
    </p>
  ),
  EUROPE_LR: (
    <p>
      <span className="font-[600]">{t("europe", "Europe")}</span>{" "}
      {t(
        "amt_area_europe_lr_desc",
        "Excluding Cyprus, Greece, Malta, Spain, Turkey, Egypt, Morocco & Tunisia"
      )}
      {isPriceToggleEnabled ? (
        <>
          . <EditStartDateButton t={t} onClick={onEditClick} />
        </>
      ) : null}
    </p>
  ),
  WORLD_LR: (
    <p>
      <span className="font-[600]">
        {t("amt_area_world_title", "All Countries")}
      </span>{" "}
      {t(
        "amt_area_world_lr_desc",
        "Excluding USA, Canada, Caribbean, Bermuda, Mexico, Thailand, China & Hong Kong"
      )}
      {isPriceToggleEnabled ? (
        <>
          . <EditStartDateButton t={t} onClick={onEditClick} />
        </>
      ) : null}
    </p>
  ),
  WORLD_HR: (
    <p>
      <span className="font-[600]">
        {t("amt_area_world_title", "All Countries")}
      </span>
      {isPriceToggleEnabled ? (
        <>
          . <EditStartDateButton t={t} onClick={onEditClick} />
        </>
      ) : null}
    </p>
  ),
});

const mobileCardsClassNames = {
  start: "px-[6px] md:px-[0px] md:pl-[24px]",
  end: "px-[6px] md:px-[0px] md:pr-[24px]",
  middle: "md:px-[12px] px-[6px]",
};

const expandedMobileCardsClassNames = {
  start: "px-[6px] tablet:px-[0px] lg:pl-[24px]",
  end: "px-[6px] tablet:px-[0px] lg:pr-[24px]",
  middle: "px-[6px] tablet:px-[0px] lg:px-[12px]",
};

const mobileCardsClassNamesForLessThreeCards = {
  start: "px-[6px] md:px-[0px] md:pl-[48px]",
  end: "px-[6px] md:px-[12px] md:pr-[48px]",
  middle: "",
};

const expandedMobileCardsClassNamesForLessThreeCards = {
  start: "px-[6px] tablet:px-[0px]",
  end: "px-[6px] tablet:px-[0px]",
  middle: "",
};

function setPatchBody(
  attributes: QuoteData["attributes"],
  postData: UpdateProps["requestdata"]
) {
  let data = attributes.tripDetails;
  if (data) data = { ...data, ...postData };
  if (data?.policyType === SINGLE_TRIP) {
    delete data.area;
  }
  return data;
}

function QuoteCover({
  quoteCoverContent,
  drawerData,
  quoteAreaData,
  isPromoBannerActive = false,
}: Readonly<{
  quoteCoverContent: CoverContentType | null;
  drawerData: ComparisonTableAreaContentType | null;
  quoteAreaData?: CoverAreaDataType;
  isPromoBannerActive?: boolean;
}>) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { showLoading, hideLoading, isLoading } = useLoading();
  const quoteData = useSelector((state: RootState) => state.quote);
  const isQuoteDisabled = useQuoteDisabled();
  const { attributes, quoteCompleted, referenceId, version } = quoteData;

  const { tripDetails } = attributes;

  const { current } = useWizard();
  const isSimplifiedHeroBanner =
    current?.uiElementControls?.isSimplifiedHeroBanner;
  const [selectedCoverLevel, setSelectedCoverLevel] = useState("");
  const [amtBasicPriceDifference, setAmtBasicPriceDifference] = useState<
    string | null
  >(null);
  const [updateCoverType, setUpdateCoverType] = useState(false);
  const [selectedTab, setSelectedTab] = useState("");
  const [autoPlay, setAutoPlay] = useState(true);
  const [isLoopComplete, setIsLoopComplete] = useState(false);
  const [dispSliderHaddleBtn, setDispSliderHaddleBtn] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 904px)");
  const isMobile = useMediaQuery("(width < 768px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 903px)");

  // Get carousel config from definition or use defaults
  const extendedCarouselBreakpoint =
    Number(current.brandSpecificParams?.extendedCarouselBreakpoint) || 1165;
  const carouselPadding600to640 = String(
    current.brandSpecificParams?.carouselCenterPadding600to640 || "10%"
  );
  const isAboveExtendedBreakpoint = useMediaQuery(
    `(min-width: ${extendedCarouselBreakpoint}px)`
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isInitialAMT, setIsInitialAMT] = useState(false);
  const [isAmtModalOpen, setIsAmtModalOpen] = useState(false);
  const [toggleKey, setToggleKey] = useState(0); // Key to force TripTypeToggle re-render
  const [initiateGtmUpdate, setInitiateGtmUpdate] = useState(false);
  const [isChangeOpen, setIsChangeOpen] = useState(false); // cancelation Change
  const [dynamicCoverLimit, setDynamicCoverLimit] = useState<
    CancellationCovers[] | null
  >(null); // cancellation Change cover limits
  const [cancelltionChangeCoverType, setCancelltionChangeCoverType] =
    useState(""); // cancelation Change cover type
  const [totalSlides, setTotalSlides] = useState<number>(0);
  const { t } = useWizardTranslation();
  const isOptimisedMobilePricingExperienceEnabled = useMemo(() => {
    return (
      isMobile && current.uiElementControls?.optimizedMobilePricingExperience
    );
  }, [current.uiElementControls?.optimizedMobilePricingExperience, isMobile]);

  const isPriceToggleEnabled = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      localStorage.getItem(
        OptimizelyExperimentsEnum.PRICE_TOGGLE_EXPERIMENT
      ) === "variant"
    );
  }, []);

  const isExapnadedTabletSelectBtnEnabled =
    current.brandSpecificParams?.isExpandedTabletSelectBtn;

  // Get brand-specific UI configuration
  const brandConfig = useMemo(() => getBrandUIConfig(PRODUCT), []);

  const shouldUseExtendedCarousel = useMemo(() => {
    return (
      brandConfig.features.enableExtendedCarousel &&
      APPLICATION === "quote" &&
      current.brandSpecificParams?.isDisplayWhichBestBuyBadge === true &&
      !isAboveExtendedBreakpoint
    );
  }, [
    brandConfig,
    current.brandSpecificParams?.isDisplayWhichBestBuyBadge,
    isAboveExtendedBreakpoint,
  ]);

  // Check if badge feature is enabled (for 600-640px carousel adjustment)
  const isBadgeFeatureEnabled = useMemo(() => {
    return (
      brandConfig.features.enableBadgeFeature &&
      APPLICATION === "quote" &&
      current.brandSpecificParams?.isDisplayWhichBestBuyBadge === true
    );
  }, [brandConfig, current.brandSpecificParams?.isDisplayWhichBestBuyBadge]);

  // Get fixed card width configuration
  const useFixedCoverCardWidths =
    current.brandSpecificParams?.useFixedCoverCardWidths === true;
  const coverCardWidthDesktop =
    Number(current.brandSpecificParams?.coverCardWidthDesktop) || 325;
  const coverCardWidthTablet =
    Number(current.brandSpecificParams?.coverCardWidthTablet) || 360;
  const coverCardWidthMobile =
    Number(current.brandSpecificParams?.coverCardWidthMobile) || 312;

  // Helper function to get responsive card width
  const getResponsiveCardWidth = (): number | undefined => {
    if (!useFixedCoverCardWidths) return undefined;
    if (isMobile) return coverCardWidthMobile;
    if (isTablet) return coverCardWidthTablet;
    return coverCardWidthTablet; // Default for other breakpoints in carousel
  };

  let sliderRefer: any = useRef(null);
  const quoteCoverLevelsRef = useRef<HTMLDivElement>(null);
  const stAmtModalRef = useRef<HTMLDivElement>(null);

  const getCoverLevelData = (tripType: string) => {
    const dataList = getRelatedCoverData(
      quoteAreaData,
      tripDetails?.goingOnCruise && isCruiseImpact,
      tripType
    ).filter((item) => item !== null)[0];
    if (dataList?.length && totalSlides === 0) {
      setTotalSlides(dataList?.length ?? 0);
    }
    return dataList;
  };

  const coverLevelItemData = useMemo(() => {
    return getCoverLevelData(selectedTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  // Calculate and store Annual Multi-Trip package price difference based on brand
  useEffect(() => {
    const priceDifference = calculateAmtPriceDifferenceByBrand(
      PRODUCT,
      attributes.priceResult?.policyPackagePrice,
      attributes.tripDetails?.product
    );

    setAmtBasicPriceDifference(priceDifference);
  }, [
    attributes.priceResult?.policyPackagePrice,
    attributes.tripDetails?.product,
  ]);

  // Check if all covers for Single Trip OR all covers for Annual Multi Trip have medical declined
  const hasAllCoversDeclined = useMemo(() => {
    if (!attributes.priceResult?.policyPackagePrice) {
      return false;
    }

    const singleTripCovers = attributes.priceResult.policyPackagePrice.filter(
      (price) => price.policyType === SINGLE_TRIP
    );
    const annualMultiTripCovers =
      attributes.priceResult.policyPackagePrice.filter(
        (price) => price.policyType === ANNUAL_MULTI_TRIP
      );

    // Check if all Single Trip covers are declined
    const allSingleTripDeclined =
      singleTripCovers.length > 0 &&
      singleTripCovers.every((price) => price.hasMedicalDeclined === true);

    // Check if all Annual Multi Trip covers are declined
    const allAnnualMultiTripDeclined =
      annualMultiTripCovers.length > 0 &&
      annualMultiTripCovers.every((price) => price.hasMedicalDeclined === true);

    return allSingleTripDeclined || allAnnualMultiTripDeclined;
  }, [attributes.priceResult?.policyPackagePrice]);

  useEffect(() => {
    if (tripDetails) {
      if (tripDetails.policyType) {
        setSelectedTab(tripDetails.policyType);
        if (
          tripDetails?.toLocation !== null &&
          tripDetails?.toLocation !== undefined
        ) {
          setIsInitialAMT(false);
        } else {
          setIsInitialAMT(true);
        }
      }

      if (
        tripDetails.policyPackage &&
        tripDetails.initialQuotePageLoading === true
      ) {
        setSelectedCoverLevel("");
      } else if (tripDetails.policyPackage) {
        setSelectedCoverLevel(tripDetails.policyPackage);
      } else {
        setSelectedCoverLevel("");
      }
    }
  }, [tripDetails]);

  useEffect(() => {
    if (sliderRefer?.innerSlider?.slideHandler && autoPlay === false) {
      const selectedCoverLevelIndex = selectedCoverLevel
        ? coverLevelItemData?.findIndex(
            (item) => item?.coverType === selectedCoverLevel
          )
        : Math.floor(totalSlides / 2) + (totalSlides % 2) - 1;
      sliderRefer?.innerSlider?.disableBodyScroll();
      setTimeout(() => {
        sliderRefer?.innerSlider?.slideHandler(selectedCoverLevelIndex);
        setDispSliderHaddleBtn(true);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, isDesktop]);

  useEffect(() => {
    if (updateCoverType === false) {
      dispatch(setCoverLevelChangedState(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCoverType]);

  const resetTrigger = useCallback(() => {
    setUpdateCoverType(false);
    hideLoading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading, error, updateTripDetails } = useUpdateTripDetails({
    resetTrigger,
  });

  const TabToggleHandler = (val: string) => {
    const requestdata: {
      policyPackage: string;
      policyType: string;
      initialQuotePageLoading: boolean;
      startDate?: string;
    } = {
      policyPackage:
        selectedCoverLevel === "" ? defaultCoverLevel : selectedCoverLevel,
      policyType: val,
      initialQuotePageLoading: !updateCoverType,
    };

    // Only set today's date when price toggle experiment is enabled
    // and switching from Single Trip to Annual Multi Trip
    if (
      isPriceToggleEnabled &&
      val === ANNUAL_MULTI_TRIP &&
      selectedTab === SINGLE_TRIP
    ) {
      const today = new Date();
      const todayFormatted = today.toISOString().split("T")[0];
      requestdata.startDate = todayFormatted;
    }

    const bodyData = setPatchBody(attributes, requestdata);
    updateTripDetails(bodyData);
  };

  useEffect(() => {
    const handleEffect = async () => {
      if (!loading && data && !error) {
        dispatch(
          updateQuote({
            ...data,
            quoteCompleted,
          })
        );
        setInitiateGtmUpdate(true);
      } else if (!loading && error !== null) {
        if (error.response?.status === 401) {
          dispatch(clearQuote());
          router.push("/");
        } else {
          const errorObject = await apiErrorMappings(error);
          errorObject.multiButton = true;
          dispatch(setApiError(errorObject));
        }
      }
    };
    handleEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, data, dispatch]);

  const [coverDrawerData, setCoverDrawerData] = useState<
    CoverDrawerDetailsDataType[]
  >([]);
  const [coverDrawerTitle, setCoverDrawerTitle] = useState<string>("");
  const [drawerCopy, setDrawerCopy] = useState<string>("");

  let gtmQuoteData = null;
  let policyPackages = null;
  if (data) {
    gtmQuoteData = QuoteDataMap(
      data.attributes,
      "select_item",
      "cover selection",
      referenceId
    );
    policyPackages = QuoteCoverTypeMap(data.attributes);
    delete policyPackages[0]?.index;
  }
  delete gtmQuoteData?.ecommerce?.value;

  useGtmQuotePolicyOptions({
    quoteData: gtmQuoteData,
    policyItems: policyPackages,
    isReady: initiateGtmUpdate,
  });

  function enableScroll() {
    const body = document.getElementsByTagName("body")[0];
    body.style.overflow = "";
    body.style.top = "";
  }

  function disableScroll() {
    const scrollPosition = [
      window?.scrollX ||
        document.documentElement.scrollLeft ||
        document.body.scrollLeft,
      window?.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop,
    ];
    const body = document.getElementsByTagName("body")[0];
    body.style.top = `-${scrollPosition[1]}px`;
  }

  function updateSelectedCoverLevel(
    val: string,
    state: boolean,
    index: number
  ) {
    if (state && !updateCoverType) {
      setSelectedCoverLevel(state ? val : "");
      if (!isDesktop) {
        if (index === 0) {
          sliderRefer?.innerSlider?.slideHandler(0);
        } else if (index === 1) {
          sliderRefer?.innerSlider?.slideHandler(1);
        } else if (index === 2) {
          sliderRefer?.innerSlider?.slideHandler(2);
        }
      }
      const requestdata = {
        policyPackage: val,
        policyType: selectedTab,
        initialQuotePageLoading: false,
      };

      const bodyData = setPatchBody(attributes, requestdata);

      updateTripDetails(bodyData);
      setUpdateCoverType(true);
      setInitiateGtmUpdate(false);
    }
  }

  const isTaxCopyBelowButton =
    current?.brandSpecificParams?.isTaxCopyBelowDetailedComparisonBtn === true;

  const commonCoverLevelCardProps = useMemo(() => {
    return {
      selectedCoverLevel,
      selectedTab,
      updateCoverType,
      setCancellationCoverOpen: setIsChangeOpen,
      setCancelltionChangeCoverType,
      setDynamicCoverLimit,
      updateSelectedCoverLevel,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, selectedCoverLevel, updateCoverType, version]);

  const { modalStyles, setIsModalOpen, setShouldCalculate } = useModalPosition(
    isDesktop
      ? ST_AMT_SWITCH_DATE_MODAL_HEIGHT.DESKTOP
      : ST_AMT_SWITCH_DATE_MODAL_HEIGHT.MOBILE,
    isDesktop
      ? ST_AMT_SWITCH_DATE_MODAL_SAFE_TOP_PERCENTAGE.DESKTOP
      : ST_AMT_SWITCH_DATE_MODAL_SAFE_TOP_PERCENTAGE.MOBILE,
    stAmtModalRef
  );

  useEffect(
    () => setIsModalOpen(isAmtModalOpen),
    [isAmtModalOpen, setIsModalOpen]
  );

  if (!quoteCoverContent || !drawerData || !quoteAreaData) return null;

  const {
    cancelationCoverCancelCta,
    cancelationCoverDescription,
    cancelationCoverTitle,
    cancelationCoverUpdateCta,
    detailedComparisonBtnLabel,
    drawerTitle,
    taxCopy,
    topToggleBtnOneLabel,
    topToggleBtnTwoLabel,
    firstOptionDescription,
    secondOptionDescription,
    stAmtToggleCancellaionContinueBtnLabele,
    stAmtToggleCancellaionDescription,
    stAmtToggleCancellaionDescriptionMoreThanNinetyDays,
    stAmtToggleCancellaionNewDateBtnLabel,
    stAmtToggleCancellaionTitle,
    stAmtToggleNewDateCancelButtonLabel,
    stAmtToggleNewDateUpdateBtnLabel,
    stAmtToggleNewDateTitle,
    stAmtToggleNewDateDatePickerPlaceHolder,
  } = quoteCoverContent;

  const renderTaxCopy = () => (
    <div
      className={`flex justify-center px-spacing-l md:px-[0px] mb-0 ${
        isTaxCopyBelowButton && "mt-spacing-m md:mt-spacing-l"
      } ${isSimplifiedHeroBanner ? "mt-spacing-m md:mt-spacing-l" : ""}`}
    >
      <div
        className={`font-body-font text-[14px] font-[400] leading-[21px] text-center text-text-functional ${
          isSimplifiedHeroBanner ? "lg:mx-0 md:mx-spacing-xxl mx-0" : ""
        }`}
      >
        <RichText
          richText={taxCopy}
          className={
            isSimplifiedHeroBanner ? "[&_p]:mb-[7px] [&_p:last-child]:mb-0" : ""
          }
        />
      </div>
    </div>
  );

  const handleTripTypeToggle = createTripTypeToggleHandler({
    tripDetails,
    selectedTab,
    TabToggleHandler,
    showLoading,
    setIsAmtModalOpen,
    isPriceToggleEnabled,
  });

  const isSideBySideToggleEnabled =
    current?.component === "quote" &&
    current?.uiElementControls?.isShowSideBySideToggle === true;

  return (
    <div data-testid="QuoteCoverSection-test-id">
      {/* AMT toggle modal */}
      {isAmtModalOpen && (
        <Modal
          onOpenChange={(e: boolean) => setIsAmtModalOpen(e)}
          isOpen={isAmtModalOpen}
          modalStyles={modalStyles}
          className={`bg-surface-neutral-option-1 md:max-w-[416px] max-w-[327px] relative rounded-radius-lg `}
          uderlayClassName={`${
            isLoading ? "z-[-1]" : "z-[100]"
          } bg-interactive-overlay-default`}
        >
          <Dialog className=" relative">
            <StAmtTogleModal
              onCancel={() => {
                setIsAmtModalOpen(false);
              }}
              onCancelModal={() => {
                setIsAmtModalOpen(false); // Force TripTypeToggle to re-render with current selectedTab value (only on X button click)
                setToggleKey((prev) => prev + 1);
              }}
              stAmtToggleCancellaionTitle={stAmtToggleCancellaionTitle}
              stAmtToggleCancellaionDescription={
                stAmtToggleCancellaionDescription
              }
              stAmtToggleCancellaionDescriptionMoreThanNinetyDays={
                stAmtToggleCancellaionDescriptionMoreThanNinetyDays
              }
              stAmtToggleCancellaionContinueBtnLabele={
                stAmtToggleCancellaionContinueBtnLabele
              }
              stAmtToggleCancellaionNewDateBtnLabel={
                stAmtToggleCancellaionNewDateBtnLabel
              }
              stAmtToggleNewDateDatePickerPlaceHolder={
                stAmtToggleNewDateDatePickerPlaceHolder
              }
              stAmtToggleNewDateTitle={stAmtToggleNewDateTitle}
              stAmtToggleNewDateUpdateBtnLabel={
                stAmtToggleNewDateUpdateBtnLabel
              }
              stAmtToggleNewDateCancelButtonLabel={
                stAmtToggleNewDateCancelButtonLabel
              }
              isOpenViaBottomToggle={false}
              onIsNewDateUpdate={setShouldCalculate}
              modalRef={stAmtModalRef}
            />
          </Dialog>
        </Modal>
      )}
      {/* cancelation cover modal */}
      {isChangeOpen && (
        <CancellationCoverModal
          isOpen={isChangeOpen}
          setIsOpen={(val) => setIsChangeOpen(val)}
          values={dynamicCoverLimit || null}
          tripType={selectedTab}
          packageType={cancelltionChangeCoverType}
          cancelationCoverTitle={cancelationCoverTitle}
          cancelationCoverDescription={cancelationCoverDescription}
          cancelationCoverUpdateCta={cancelationCoverUpdateCta}
          cancelationCoverCancelCta={cancelationCoverCancelCta}
        />
      )}
      <div
        className={`max-w-[1200px] lg:mx-auto mx-0 my-0 w-[100%] relative lg:bg-surface-neutral-option-1 ${
          isPriceToggleEnabled
            ? "lg:pl-spacing-xxl lg:pr-spacing-xxl lg:pb-spacing-xl lg:pt-spacing-xl md:pt-spacing-xl sm:pt-spacing-xl"
            : "lg:p-spacing-xxl lg:pt-spacing-xxl"
        } p-[0px] rounded-radius-xxl ${
          isPromoBannerActive ? "" : "lg:mt-spacing-xxl"
        }`}
        ref={quoteCoverLevelsRef}
      >
        {!isInitialAMT && !isPriceToggleEnabled && !hasAllCoversDeclined && (
          <div
            id="AMT-ST-TOGGLE-MAIN-WRAPPER-ID"
            data-testid="AMT-ST-TOGGLE-MAIN-WRAPPER-ID"
            className={`${
              current.brandSpecificParams?.isShowTripTypeToggle
                ? "w-full max-w-[800px]"
                : "w-[280px]"
            } mx-auto md:mb-spacing-xs mb-spacing-[0px] lg:mt-[0px] ${
              isPromoBannerActive
                ? "mt-spacing-m lg:mt-spacing-xl"
                : "mt-spacing-l md:mt-spacing-xl"
            } ${
              current.brandSpecificParams?.isShowTripTypeToggle
                ? "px-spacing-xs"
                : ""
            }`}
          >
            {current.brandSpecificParams?.isShowTripTypeToggle ? (
              <TripTypeToggle
                key={toggleKey}
                data-testid="AMT-ST-TOGGLE-BTN-ID"
                firstOptionId={SINGLE_TRIP}
                firstOptionLabel={topToggleBtnOneLabel}
                firstOptionValue={SINGLE_TRIP}
                firstOptionDescription={firstOptionDescription}
                firstOptionIcon={
                  <brandConfig.icons.singleTrip.nonSelected
                    width={34}
                    height={34}
                  />
                }
                firstOptionActiveIcon={
                  <brandConfig.icons.singleTrip.selected
                    width={34}
                    height={34}
                  />
                }
                secondOptionId={ANNUAL_MULTI_TRIP}
                secondOptionLabel={topToggleBtnTwoLabel}
                secondOptionValue={ANNUAL_MULTI_TRIP}
                secondOptionDescription={secondOptionDescription?.replace(
                  "XX",
                  `${current.brandSpecificParams?.currencySymbol as string}${
                    amtBasicPriceDifference ?? ""
                  }`
                )}
                secondOptionIcon={
                  <brandConfig.icons.annualMultiTrip.nonSelected
                    width={34}
                    height={34}
                  />
                }
                secondOptionActiveIcon={
                  <brandConfig.icons.annualMultiTrip.selected
                    width={34}
                    height={34}
                  />
                }
                defaultActiveValue={selectedTab}
                onToggle={handleTripTypeToggle}
                isDisabled={isQuoteDisabled}
                isSideBySide={isSideBySideToggleEnabled}
              />
            ) : (
              <ButtonTabWrapper
                data-testid="AMT-ST-TOGGLE-BTN-ID"
                tabs={createBtnTabs(topToggleBtnOneLabel, topToggleBtnTwoLabel)}
                className="font-body-font font-[700] h-[40px] text-[16px] lg:bg-surface-neutral-option-2 bg-surface-neutral-option-1"
                defaultActiveTab={selectedTab}
                isDefaultTogle={false}
                onClick={handleTripTypeToggle}
                isDisabled={isQuoteDisabled}
              />
            )}
          </div>
        )}

        {tripDetails?.policyType === ANNUAL_MULTI_TRIP && !isInitialAMT && (
          <div
            className={`text-[#000000] text-[14px] font-[400] leading-[21px] font-body-font mx-auto px-spacing-l md:px-0 ${
              isPriceToggleEnabled ? "" : "mb-spacing-m mt-spacing-m"
            } pb-spacing-xs`}
          >
            <p className="text-center">
              {isPriceToggleEnabled ? (
                <>
                  <span className="font-[600]">
                    {t(
                      "amt_cover_start_bold",
                      "Cover for this policy will start today"
                    )}
                  </span>
                  {", "}
                  {t("amt_cover_description", "travelling to")}{" "}
                </>
              ) : (
                <>
                  {t(
                    "these_policies_cover",
                    "These policies cover each insured person for:"
                  )}
                  <br />
                </>
              )}
              {
                AmtAreaText(t, isPriceToggleEnabled, () => {
                  router.push("/?coverEdit=true");
                })[
                  (tripDetails?.area as
                    | "EUROPE_HR"
                    | "EUROPE_LR"
                    | "WORLD_LR"
                    | "WORLD_HR"
                    | "UK") ?? "WORLD_LR"
                ]
              }
            </p>
          </div>
        )}

        {/* Build hide configuration based on API response */}
        {(() => {
          type PackageType =
            | "basic"
            | "comprehensive"
            | "signature"
            | "essentials"
            | "classic"
            | "deluxe";

          const buildHideConfig = (): Record<PackageType, boolean> => {
            const apiHideConfig: Record<PackageType, boolean> = {
              basic: false,
              comprehensive: false,
              signature: false,
              essentials: false,
              classic: false,
              deluxe: false,
            };

            // Only apply hide logic when enableMedicalDeclinedFlow is true
            if (current.brandSpecificParams?.enableMedicalDeclinedFlow) {
              attributes?.priceResult?.policyPackagePrice?.forEach((price) => {
                const packageType =
                  price.policyPackage?.toLowerCase() as PackageType;
                // Hide cards where hasMedicalDeclined is true
                // Only process cards matching the current selected trip type (selectedTab)
                if (
                  price.hasMedicalDeclined &&
                  packageType in apiHideConfig &&
                  price.policyType === selectedTab
                ) {
                  apiHideConfig[packageType] = true;
                }
              });
            }

            return apiHideConfig;
          };

          const hideConfig = buildHideConfig();

          /**
           * Type guard to extract and validate card type
           * Ensures type safety and reusability
           * Supports both Staysure (basic, comprehensive, signature) and Avanthi (essentials, classic, deluxe) naming
           */
          const getCardType = (coverType?: string): PackageType | null => {
            const normalized = coverType?.toLowerCase();
            if (
              normalized === "basic" ||
              normalized === "comprehensive" ||
              normalized === "signature" ||
              normalized === "essentials" ||
              normalized === "classic" ||
              normalized === "deluxe"
            ) {
              return normalized as PackageType;
            }
            return null;
          };

          /**
           * Predicate function to check if a card should be visible
           * Single source of truth for visibility logic
           */
          const isCardVisible = (
            item: (typeof coverLevelItemData)[number]
          ): boolean => {
            const cardType = getCardType(item.coverType);
            return cardType ? !hideConfig[cardType] : true;
          };

          /**
           * Filter visible cards once and reuse for both desktop and mobile
           * Optimizes performance by avoiding duplicate filtering
           */
          const visibleCards = coverLevelItemData?.filter(isCardVisible) || [];
          const visibleCardsCount = visibleCards.length;

          /**
           * Renders a quote card with consistent props across all instances
           * Eliminates code duplication and ensures consistency
           * @param item - Cover level data item
           * @param index - Item index in the list
           * @param drawerTitleOverride - Optional override for drawer title (defaults to item.coverTitle)
           */
          const renderQuoteCard = (
            item: (typeof coverLevelItemData)[number],
            index: number,
            drawerTitleOverride?: string
          ) => (
            <MapQuoteCard
              key={index as unknown as string}
              {...quoteCoverContent}
              {...commonCoverLevelCardProps}
              type={index}
              coverType={item.coverType}
              coverTitle={item.coverTitle}
              benifitData={item.benifitData as unknown as BenefitCopyDataType[]}
              coverDrawerTitle={drawerTitleOverride || item.coverTitle}
              coverDrawerCopy={item.coverDrawerCopy}
              coverDrawerDetails={
                item.coverDrawerDetails as unknown as CoverDrawerDetailsDataType[]
              }
              defaqtoRating={item.defaqtoRating as unknown as 1 | 2 | 3 | 4 | 5}
              whichBestBuyBadge={item.whichBestBuyBadge}
              isHideCard={hideConfig}
              onTripTypeToggle={handleTripTypeToggle}
            />
          );

          return (
            <>
              {/* desktop - exclude 904-1165px when extended carousel enabled */}
              {isDesktop &&
                !shouldUseExtendedCarousel &&
                !isOptimisedMobilePricingExperienceEnabled && (
                  <>
                    {visibleCardsCount === 1 ? (
                      // Single card centered with shadow
                      <div className="flex justify-center px-spacing-l">
                        <div
                          className={
                            useFixedCoverCardWidths
                              ? "rounded-[16px] bg-transparent"
                              : "lg:min-w-[720px] rounded-[16px] bg-transparent"
                          }
                          style={
                            useFixedCoverCardWidths
                              ? {
                                  width: `${coverCardWidthDesktop}px`,
                                }
                              : undefined
                          }
                        >
                          {visibleCards.map((item, index) =>
                            renderQuoteCard(item, index)
                          )}
                        </div>
                      </div>
                    ) : (
                      // Multiple cards with gap spacing
                      <div
                        className={`hidden lg:flex lg:justify-center gap-spacing-s items-start`}
                      >
                        {visibleCards.map((item, index) => (
                          <div
                            key={index as unknown as string}
                            className={useFixedCoverCardWidths ? "" : "flex-1"}
                            style={
                              useFixedCoverCardWidths
                                ? {
                                    width: `${coverCardWidthDesktop}px`,
                                    flexShrink: 0,
                                  }
                                : {
                                    maxWidth: `${
                                      100 / Math.max(visibleCardsCount, 1)
                                    }%`,
                                  }
                            }
                          >
                            {renderQuoteCard(item, index)}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

              {/* tablet & mobile - include 904-1165px when extended carousel enabled */}
              {(!isDesktop || shouldUseExtendedCarousel) &&
                !isOptimisedMobilePricingExperienceEnabled && (
                  <>
                    {visibleCardsCount === 1 ? (
                      // Single card centered with shadow for mobile
                      <div className="flex justify-center px-spacing-l">
                        <div
                          className={
                            useFixedCoverCardWidths
                              ? "rounded-[16px] bg-transparent"
                              : "max-w-[720px] w-full rounded-[16px] bg-transparent"
                          }
                          style={
                            useFixedCoverCardWidths
                              ? {
                                  width: `${getResponsiveCardWidth()}px`,
                                }
                              : undefined
                          }
                        >
                          {visibleCards.map((item, index) =>
                            renderQuoteCard(item, index, item.coverType)
                          )}
                        </div>
                      </div>
                    ) : isTablet && isExapnadedTabletSelectBtnEnabled ? (
                      // Tablet horizontal scroll layout
                      <div className="overflow-x-auto pl-[48px] pr-[48px] pb-spacing-m [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                        <div className="flex gap-[12px]">
                          {visibleCards.map((item, index) => {
                            const cardWidth = getResponsiveCardWidth();
                            return (
                              <div
                                key={index as unknown as string}
                                style={
                                  cardWidth
                                    ? {
                                        width: `${cardWidth}px`,
                                        flexShrink: 0,
                                      }
                                    : undefined
                                }
                              >
                                {renderQuoteCard(item, index, item.coverType)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      // Multiple cards with slider
                      <div
                        className={`${
                          shouldUseExtendedCarousel ? "" : "lg:hidden"
                        } relative`}
                        data-testid="tablet-and-mobile-slider"
                      >
                        <Slider
                          className={` center`}
                          ref={function (slider: any) {
                            sliderRefer = slider;
                          }}
                          centerMode={visibleCardsCount > 2}
                          infinite={false}
                          slidesToShow={visibleCardsCount > 2 ? 1 : 2}
                          initialSlide={0}
                          autoplay={visibleCardsCount > 2 ? autoPlay : false}
                          speed={1000}
                          autoplaySpeed={1000}
                          arrows={false}
                          verticalSwiping={false}
                          centerPadding={visibleCardsCount > 2 ? "25%" : "0px"}
                          accessibility
                          afterChange={() => {
                            if (isLoopComplete && visibleCardsCount > 2) {
                              setAutoPlay(false);
                            }
                            enableScroll();
                          }}
                          beforeChange={(current: number, next: number) => {
                            disableScroll();
                            if (
                              next === visibleCardsCount - 2 &&
                              visibleCardsCount > 2
                            ) {
                              setIsLoopComplete(true);
                            }
                          }}
                          responsive={[
                            // Conditionally include 640px breakpoint only for Staysure QNB
                            ...(isBadgeFeatureEnabled
                              ? [
                                  {
                                    breakpoint: 640,
                                    settings: {
                                      slidesToShow: 1,
                                      centerMode: true,
                                      verticalSwiping: false,
                                      arrows: true,
                                      autoplay: autoPlay,
                                      nextArrow: (
                                        <SlickButton
                                          type="next"
                                          btnDisplay={dispSliderHaddleBtn}
                                          isCoverLevelSelected={
                                            selectedCoverLevel !== ""
                                          }
                                        />
                                      ),
                                      prevArrow: (
                                        <SlickButton
                                          type="prev"
                                          btnDisplay={dispSliderHaddleBtn}
                                          isCoverLevelSelected={
                                            selectedCoverLevel !== ""
                                          }
                                        />
                                      ),
                                      centerPadding: carouselPadding600to640,
                                      afterChange: () => {
                                        if (isLoopComplete) {
                                          setAutoPlay(false);
                                        }
                                        enableScroll();
                                      },
                                      beforeChange: (
                                        current: number,
                                        next: number
                                      ) => {
                                        disableScroll();
                                        if (next === visibleCardsCount - 2) {
                                          setIsLoopComplete(true);
                                        }
                                      },
                                    },
                                  },
                                ]
                              : []),
                            {
                              breakpoint: 600,
                              settings: {
                                slidesToShow: 1,
                                centerMode: true,
                                verticalSwiping: false,
                                arrows: true,
                                autoplay: autoPlay,
                                nextArrow: (
                                  <SlickButton
                                    type="next"
                                    btnDisplay={dispSliderHaddleBtn}
                                    isCoverLevelSelected={
                                      selectedCoverLevel !== ""
                                    }
                                  />
                                ),
                                prevArrow: (
                                  <SlickButton
                                    type="prev"
                                    btnDisplay={dispSliderHaddleBtn}
                                    isCoverLevelSelected={
                                      selectedCoverLevel !== ""
                                    }
                                  />
                                ),
                                centerPadding: "18px",
                                afterChange: () => {
                                  if (isLoopComplete) {
                                    setAutoPlay(false);
                                  }
                                  enableScroll();
                                },
                                beforeChange: (
                                  current: number,
                                  next: number
                                ) => {
                                  disableScroll();
                                  if (next === visibleCardsCount - 2) {
                                    setIsLoopComplete(true);
                                  }
                                },
                              },
                            },
                          ]}
                        >
                          {visibleCards.map((item, index) => {
                            const cardPosition = (indexId: number) => {
                              if (indexId === 0) return "start";
                              if (indexId === visibleCardsCount - 1)
                                return "end";
                              return "middle";
                            };

                            const cardWidth = getResponsiveCardWidth();

                            return (
                              <div
                                key={index as unknown as string}
                                className={
                                  visibleCardsCount < 3
                                    ? isExapnadedTabletSelectBtnEnabled
                                      ? expandedMobileCardsClassNamesForLessThreeCards[
                                          cardPosition(index)
                                        ]
                                      : mobileCardsClassNamesForLessThreeCards[
                                          cardPosition(index)
                                        ]
                                    : isExapnadedTabletSelectBtnEnabled
                                    ? expandedMobileCardsClassNames[
                                        cardPosition(index)
                                      ]
                                    : mobileCardsClassNames[cardPosition(index)]
                                }
                                style={
                                  cardWidth
                                    ? {
                                        width: `${cardWidth}px`,
                                      }
                                    : undefined
                                }
                              >
                                {renderQuoteCard(item, index, item.coverType)}
                              </div>
                            );
                          })}
                        </Slider>
                      </div>
                    )}
                  </>
                )}
            </>
          );
        })()}

        {isOptimisedMobilePricingExperienceEnabled && (
          <MobileQuoteComparison
            {...quoteCoverContent}
            {...commonCoverLevelCardProps}
            quoteAreaData={quoteAreaData}
            selectedTab={selectedTab}
            drawerData={drawerData}
            onlineExclusiveBannerLabel={
              quoteCoverContent.onlineExclusiveBannerLabel
            }
            onSeeMoreDetails={(index) => {
              const item = coverLevelItemData?.[index];
              if (item) {
                setCoverDrawerTitle(item.coverTitle ?? "");
                setDrawerCopy(item.coverDrawerCopy ?? "");
                setCoverDrawerData(
                  (item.coverDrawerDetails as CoverDrawerDetailsDataType[]) ||
                    []
                );
                setIsOpen(true);
              }
            }}
          />
        )}
        {!isSimplifiedHeroBanner && !isTaxCopyBelowButton && renderTaxCopy()}

        {!isOptimisedMobilePricingExperienceEnabled &&
          !hasOnlyOneCoverEligible(
            tripDetails?.policyType,
            attributes?.priceResult?.policyPackagePrice
          ) && (
            <div className=" md:w-[265px] w-full px-spacing-l md:px-0 mx-auto ">
              <LabelButtonWrapper
                data-testid="showDetailedComparisonBtn"
                id="showDetailedComparisonBtn"
                type="button"
                hierarchy="secondary"
                label={detailedComparisonBtnLabel}
                className="!border-[1.5px] text-[18px] leading-[18px] font-[600] bg-surface-neutral-option-1"
                onClick={() => {
                  setIsOpen(true);
                  document.getElementById("showDetailedComparisonBtn")?.blur();
                }}
                isDisabled={isQuoteDisabled}
              />
            </div>
          )}
        {!isSimplifiedHeroBanner && isTaxCopyBelowButton && renderTaxCopy()}

        {isSimplifiedHeroBanner && renderTaxCopy()}
        {/* <MobileQuoteComparison
          {...quoteCoverContent}
          {...commonCoverLevelCardProps}
          quoteAreaData={quoteAreaData}
          selectedTab={selectedTab}
          drawerData={drawerData}
          onlineExclusiveBannerLabel={
            quoteCoverContent.onlineExclusiveBannerLabel
          }
        /> */}
      </div>
      <CancellationCoverApiErrorModal brand={brand} />

      {!isOptimisedMobilePricingExperienceEnabled && (
        <ComparisionSideDrawer
          drawerTitle={drawerTitle}
          drawerData={drawerData}
          selectedTab={selectedTab}
          isOpen={isOpen}
          setOpen={setIsOpen}
        />
      )}

      {isOptimisedMobilePricingExperienceEnabled && (
        <CoverDetailsDrawer
          closeDrawerCta={t("close", "Close")}
          coverLevelTitle={coverDrawerTitle}
          isOpen={isOpen}
          setOpen={(e) => setIsOpen(e)}
          drawerCopy={drawerCopy}
          drawerData={coverDrawerData}
        />
      )}
    </div>
  );
}

export default QuoteCover;
